import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ChannelCodec,
  Resolution,
  ResolutionPreset,
} from "../../../../domain/channel-codec";
import {
  AudioCodec,
  ChannelFormat,
  VideoCodec,
} from "../../../../domain/channel";

@Component({
  selector: "icn-edit-codec-popup",
  templateUrl: "./edit-codec-popup.component.html",
  styleUrls: ["./edit-codec-popup.component.scss"],
  host: {
    class: "popup",
  },
})
export class EditCodecPopupComponent implements OnInit {
  DEFAULT_RESOLUTION_PRESET = new ResolutionPreset("CUSTOM", -1, -1);
  @ViewChild("codecNameDiv", { static: true }) codecNameDiv: ElementRef;

  @Input()
  formatDetails: ChannelFormat;

  @Input()
  codec: ChannelCodec;

  @Output() close = new EventEmitter();
  @Output() save = new EventEmitter<ChannelCodec>();

  onCodecEdit = false;
  selectedAudioCodec: AudioCodec;
  selectedVideoCodec: VideoCodec;
  channels;

  selectedResolutionPreset: ResolutionPreset = this.DEFAULT_RESOLUTION_PRESET;

  ngOnInit() {
    if (this.formatDetails) {
      this.selectedAudioCodec = this.formatDetails.audio.find(
        (x) => x.value === this.codec.audio.codecType,
      );
      this.selectedVideoCodec = this.formatDetails.video.find(
        (x) => x.value === this.codec.video.codecType,
      );

      const currentCodecResolutionAsPreset: ResolutionPreset =
        this.selectedVideoCodec.resolutions.find((x) =>
          this.resolutionEqualFunc(x, this.codec.video.resolution),
        );
      if (currentCodecResolutionAsPreset) {
        this.selectedResolutionPreset =
          currentCodecResolutionAsPreset.duplicate();
      }
    }
  }

  onCodecEditModeExit(name) {
    this.codec.name = name;
    this.codecNameDiv.nativeElement.setAttribute("contenteditable", false);
    this.onCodecEdit = false;
  }

  onChangeClick() {
    this.onCodecEdit = true;
    this.codecNameDiv.nativeElement.setAttribute("contenteditable", true);
    this.codecNameDiv.nativeElement.focus();
  }

  resolutionEqualFunc(a, b) {
    return a.width === b.width && a.height === b.height;
  }

  onSave() {
    if (this.codec.isDefault) {
      return;
    }
    this.save.emit(this.codec);
  }

  onAudioCodecChange(audioCodec: AudioCodec) {
    this.selectedAudioCodec = audioCodec;

    this.codec.audio.samplerate = this.fixEnumValue(
      this.codec.audio.samplerate,
      audioCodec.sampleRate,
    );
    this.codec.audio.bitrate = this.fixEnumValue(
      this.codec.audio.bitrate,
      audioCodec.bitRate,
    );
    this.codec.audio.channels = this.fixEnumValue(
      this.codec.audio.channels,
      audioCodec.channels.map((x) => x.channels),
    );
  }

  onVideoCodecChange(videoCodec: VideoCodec) {
    this.selectedVideoCodec = videoCodec;

    this.codec.video.fps = this.fixEnumValue(
      this.codec.video.fps,
      videoCodec.fps,
    );
    this.codec.video.resolution = this.fixResolution(
      this.codec.video.resolution,
      videoCodec.resolutions,
    );
  }

  fixEnumValue(currentValue: number, values: number[]) {
    let result = values[0];
    values.forEach((value) => {
      if (Math.abs(value - currentValue) < Math.abs(result - currentValue)) {
        result = value;
      }
    });
    return result;
  }

  fixResolution(
    currentValue: { width: number; height: number },
    values: ResolutionPreset[],
  ) {
    let result = values[0];
    values.forEach((value) => {
      if (
        Math.abs(value.width - currentValue.width) +
          Math.abs(value.height - currentValue.height) <
        Math.abs(result.width - currentValue.width) +
          Math.abs(result.height - currentValue.height)
      ) {
        result = value;
      }
    });
    return result;
  }

  onResolutionPresetChanged(newResolutionPreset: ResolutionPreset) {
    if (
      this.resolutionEqualFunc(
        newResolutionPreset,
        this.DEFAULT_RESOLUTION_PRESET,
      ) === false
    ) {
      this.codec.video.resolution =
        newResolutionPreset.duplicate() as Resolution;
    }
  }

  onResolutionValueChanged() {
    const valudAsResolutionPreset: ResolutionPreset =
      this.selectedVideoCodec.resolutions.find((x) =>
        this.resolutionEqualFunc(x, this.codec.video.resolution),
      );
    if (valudAsResolutionPreset) {
      this.selectedResolutionPreset = valudAsResolutionPreset.duplicate();
    } else {
      this.selectedResolutionPreset = this.DEFAULT_RESOLUTION_PRESET;
    }
  }
}
